import { useState, useEffect, useCallback, useMemo } from 'react'
import { User } from 'firebase/auth'
import {
  signIn as authSignIn,
  signInWithGoogle as authSignInWithGoogle,
  signOut as authSignOut,
  onAuthChange
} from '@/services/auth'
import { userApi, B18User } from '@/services/api'
import { AuthContextType } from '@/types/auth'
import { logger } from '@/utils/logger'
import { hasPermission } from '@/utils/permissions'

// Every useAuth caller gets its own auth callback, so they share one B18 login per Firebase uid
let sharedLogin: { uid: string; promise: Promise<B18User | null> } | null = null

const forgetLogin = (promise: Promise<B18User | null>) => {
  if (sharedLogin?.promise === promise) sharedLogin = null
}

const loginToB18 = (firebaseUser: User): Promise<B18User | null> => {
  if (sharedLogin?.uid === firebaseUser.uid) return sharedLogin.promise

  const promise = (async () =>
    userApi.login(firebaseUser.email || '', firebaseUser.uid))()
  sharedLogin = { uid: firebaseUser.uid, promise }
  // A failed or empty login is not kept, so the next auth event tries again
  promise.then(
    result => {
      if (!result) forgetLogin(promise)
    },
    () => forgetLogin(promise)
  )
  return promise
}

export const useAuth = (): AuthContextType => {
  const [user, setUser] = useState<User | null>(null)
  const [b18User, setB18User] = useState<B18User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthChange(async firebaseUser => {
      setUser(firebaseUser)

      if (firebaseUser) {
        // Fetch B18 user data with permissions
        try {
          const b18UserData = await loginToB18(firebaseUser)
          setB18User(b18UserData)

          if (!b18UserData) {
            logger.warn('Firebase user not found in B18 database', {
              email: firebaseUser.email
            })
          }
        } catch (err) {
          logger.error('Failed to fetch B18 user', err as Error, {
            component: 'useAuth'
          })
          setB18User(null)
        }
      } else {
        sharedLogin = null
        setB18User(null)
      }

      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true)
      setError(null)
      await authSignIn(email, password)
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const signInWithGoogle = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      await authSignInWithGoogle()
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      setB18User(null)
      await authSignOut()
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Check if user has permission to read loads — `load.read`, from the rules when sent
  const canReadLoad = useMemo(
    () => hasPermission(b18User, 'load', 'read'),
    [b18User]
  )

  return {
    user,
    b18User,
    loading,
    error,
    signIn,
    signInWithGoogle,
    signOut,
    canReadLoad
  }
}

export default useAuth
